/* DC loader: the wordmark emits grain, the way JJ's Spline reference does.

   The reference is a particle emitter whose SHAPE is a text mesh: grain is continuously
   born on the letterforms and drifts off with curl noise, so the word reads as a living
   field rather than a frozen picture. This is that, in canvas 2D — no Spline, no library.

   Colour is sampled from the wordmark itself, so DIECAST comes through white and SHOW
   comes through red without hard-coding either.

   Every exit path leads to finish(): the build completing, the page loading, a missing
   image, a thrown error, and a hard timer. The snippet also arms its own timer before this
   file is fetched, so the overlay comes off even if this never runs. */
(() => {
  const el = document.getElementById('dc-loader');
  if (!el) return;
  const html = document.documentElement;
  let ended = false;
  const done = () => {
    if (ended) return;
    ended = true;
    html.classList.remove('dc-loading');
    el.classList.add('is-out');
    setTimeout(() => { try { el.remove(); } catch (e) {} }, 700);
    /* The lock held the page at the top with no scrollbar while ScrollTrigger took its
       load-time measurements, and it swallowed any fragment in the URL. Now that the page
       can scroll: measure again, then honour the fragment, then tell anyone listening. */
    requestAnimationFrame(() => {
      try { window.__dcEvent && window.__dcEvent.refresh(); } catch (e) {}
      try {
        const id = decodeURIComponent((location.hash || '').slice(1));
        const target = id && document.getElementById(id);
        if (target) {
          if (window.__dcEvent) window.__dcEvent.scrollTo(target, { immediate: true });
          else target.scrollIntoView();
        }
      } catch (e) {}
      try { document.dispatchEvent(new CustomEvent('dc:loader:done')); } catch (e) {}
    });
  };

  /* Once per session, unless asked for with ?loader=1. */
  let force = false;
  try { force = /[?&]loader=1/.test(location.search); } catch (e) {}
  let seen = false;
  try { seen = sessionStorage.getItem('dc-loader') === '1'; } catch (e) {}
  if (seen && !force) { done(); return; }
  try { sessionStorage.setItem('dc-loader', '1'); } catch (e) {}

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canvas = el.querySelector('canvas');
  const ctx = canvas && canvas.getContext('2d');
  const tag = document.querySelector('script[data-mark]');
  const src = tag && tag.dataset.mark;
  if (!ctx || !src) { setTimeout(done, 400); return; }

  const BUILD = 1400;     // ms for the field to reach full density
  const HOLD = 700;       // ms the built word lives before the hand-over
  const LIFE = 1500;      // ms a grain lives
  /* The banner is a much larger, busier mark than the small wordmark it replaced, so it
     needs more grain to read as solid rather than sparse. */
  const COUNT = 4600;     // grains alive at full density
  /* Drift has to stay well under a letter height or the word stops being a word: at 26
     px/s over a 1.5 s life a grain travelled about 39 px, which is most of a capital on a
     phone, and the mark read as a smear. 10 keeps the letterforms while the edges still
     visibly breathe. */
  const DRIFT = 10;
  const SWIRL = 1.2;      // how much the curl bends that travel

  let W = 0, H = 0, dpr = 1;
  const fit = () => {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = window.innerWidth; H = window.innerHeight;
    canvas.width = Math.max(1, Math.round(W * dpr));
    canvas.height = Math.max(1, Math.round(H * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  fit();

  const img = new Image();
  img.decoding = 'async';
  img.onerror = () => setTimeout(done, 300);
  img.onload = () => { try { start(); } catch (e) { done(); } };
  img.src = src;

  /* Every lit pixel of the mark, with the colour it is drawn in. */
  function sampleMark() {
    const markW = Math.min(W * 0.84, 660);
    const markH = markW * (img.naturalHeight / img.naturalWidth);
    const off = document.createElement('canvas');
    off.width = Math.max(1, Math.round(markW));
    off.height = Math.max(1, Math.round(markH));
    const octx = off.getContext('2d', { willReadFrequently: true });
    octx.drawImage(img, 0, 0, off.width, off.height);
    const d = octx.getImageData(0, 0, off.width, off.height).data;
    const ox = (W - off.width) / 2, oy = (H - off.height) / 2;
    const step = Math.max(1, Math.round(off.width / 260));
    const pts = [];
    for (let y = 0; y < off.height; y += step) {
      for (let x = 0; x < off.width; x += step) {
        const i = (y * off.width + x) * 4;
        const r = d[i], g = d[i + 1], b = d[i + 2];
        /* Only the solid letterforms emit. The banner carries a dark tyre-track texture
           and hairline outlines inside the type; sampled on alpha alone those became
           bright grains and filled the counters of the letters with noise. */
        if (d[i + 3] > 140 && Math.max(r, g, b) > 105) {
          const red = r > 105 && g < 95 && b < 95;
          pts.push({
            x: ox + x, y: oy + y,
            c: red ? 'rgba(255,58,58,' : 'rgba(233,238,236,',
          });
        }
      }
    }
    return pts;
  }

  /* A cheap curl: two hashed sine fields, so the drift swirls instead of raying out. */
  const flow = (x, y) => {
    const a = Math.sin(x * 0.013) * Math.cos(y * 0.017) * SWIRL;
    const b = Math.cos(x * 0.011) * Math.sin(y * 0.015) * SWIRL;
    return [a, b];
  };

  function start() {
    const pts = sampleMark();
    if (!pts.length) { done(); return; }

    if (reduced) {
      ctx.clearRect(0, 0, W, H);
      pts.forEach((p) => { ctx.fillStyle = p.c + '1)'; ctx.fillRect(p.x, p.y, 1.8, 1.8); });
      setTimeout(done, 600);
      return;
    }

    const P = [];
    const spawn = (born) => {
      const s = pts[(Math.random() * pts.length) | 0];
      return { sx: s.x, sy: s.y, x: s.x, y: s.y, c: s.c, born,
        vx: (Math.random() - 0.5) * 0.7, vy: (Math.random() - 0.5) * 0.7,
        life: LIFE * (0.65 + Math.random() * 0.7), sz: 0.9 + Math.random() * 1.2 };
    };
    for (let i = 0; i < COUNT; i++) P.push(spawn(-Math.random() * LIFE));

    const t0 = performance.now();
    let pageReady = document.readyState === 'complete';
    if (!pageReady) window.addEventListener('load', () => { pageReady = true; }, { once: true });
    /* Measured from the snippet's parse-time stamp, not from the image's arrival, so this
       always lands before the snippet's 8 s failsafe: a slow banner used to push it past
       the failsafe, which then cut the overlay with no fade. */
    const hardStop = (typeof window.__dcLoaderT0 === 'number' ? window.__dcLoaderT0 : t0) + 5400;
    let prev = t0;

    const frame = (now) => {
      if (ended || !document.getElementById('dc-loader')) return;
      const t = now - t0;
      const dt = Math.min((now - prev) / 1000, 0.05);
      prev = now;
      /* Density ramps in, so the word gathers rather than appearing whole. */
      const build = t < BUILD ? t / BUILD : 1;
      const alive = Math.round(COUNT * build);

      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < alive; i++) {
        const p = P[i];
        let age = now - p.born;
        if (age > p.life || age < 0) {
          if (age > p.life) { P[i] = spawn(now); }
          continue;
        }
        const k = age / p.life;
        const [fx, fy] = flow(p.x, p.y);
        p.vx += fx * dt; p.vy += fy * dt;
        p.x += p.vx * DRIFT * dt; p.y += p.vy * DRIFT * dt;
        /* In fast, out slow: the letterform stays legible while the edges dissolve. */
        const a = k < 0.16 ? k / 0.16 : 1 - (k - 0.16) / 0.84;
        ctx.fillStyle = p.c + (a * 0.9).toFixed(3) + ')';
        ctx.fillRect(p.x, p.y, p.sz, p.sz);
      }

      const built = t > BUILD + HOLD;
      if ((built && pageReady) || now > hardStop) { done(); return; }
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  window.addEventListener('resize', () => { try { fit(); } catch (e) {} }, { passive: true });
})();
